import React from 'react';
import { CircularProgress, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import XtramModal, { XtramButton, XtramTable, xtramStyles } from '../../Widgets/XtramModal/XtramModal';
import { beautifyNumber } from '../../../Utils/FormatNumber';
import styles from '../../BankMatching/BankXtram.module.scss';

const collectUniqueAccountValues = (rows, field) => [
    ...new Set(
        (rows || [])
            .map((row) => row?.[field])
            .filter((value) => value != null && value !== '')
    ),
];

const formatAccountValues = (values, formatAccountLabel) => {
    if (!values.length) return '-';
    return values
        .map((value) => (formatAccountLabel ? formatAccountLabel(value) : value))
        .join(', ');
};

const TransactionRowsTable = ({ rows, formatDate, t, typeLabel, amountColumnLabel }) => {
    if (!rows?.length) {
        return <p className={xtramStyles.muted}>{t('NoData')}</p>;
    }

    return (
        <XtramTable>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>{t('TransactionDate')}</TableCell>
                        <TableCell>{t('Description')}</TableCell>
                        <TableCell>{t('Reference')}</TableCell>
                        <TableCell>{t('Type')}</TableCell>
                        <TableCell>{amountColumnLabel}</TableCell>
                        <TableCell>{t('UploadNumber')}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row) => (
                        <TableRow key={`${typeLabel}-${row.id}`} hover>
                            <TableCell>{row.documentDate ? formatDate(row.documentDate) : '-'}</TableCell>
                            <TableCell>{row.description || '-'}</TableCell>
                            <TableCell>{row.reference || '-'}</TableCell>
                            <TableCell>{row.type ? t(row.type) : '-'}</TableCell>
                            <TableCell className={xtramStyles.numCell}>{beautifyNumber(row.amount)}</TableCell>
                            <TableCell>{row.uploadNumber ?? '-'}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </XtramTable>
    );
};

const ViewTransactionMatchModal = ({
    open,
    onClose,
    match,
    loading,
    formatDate,
    t,
    direction = 'rtl',
    formatAccountLabel,
    isForeignCurrency = false,
    onRemoveMatch,
    isRemovingMatch = false,
}) => {
    const internalRows = match?.internalTransactions || [];
    const amountColumnLabel = isForeignCurrency ? t('CurrencyAmount') : t('Amount');
    const details = match ? [
        { label: t('MatchNumber'), value: match.matchNumber },
        { label: t('TransactionDate'), value: match.transactionDate ? formatDate(match.transactionDate) : '-' },
        { label: t('MatchDate'), value: match.matchDate ? formatDate(match.matchDate) : '-' },
        { label: t('AccountCardNumber'), value: match.accountCodeId || '-' },
        {
            label: t('Debit Account'),
            value: formatAccountValues(collectUniqueAccountValues(internalRows, 'debitAccount'), formatAccountLabel),
        },
        {
            label: t('Credit Account'),
            value: formatAccountValues(collectUniqueAccountValues(internalRows, 'creditAccount'), formatAccountLabel),
        },
    ] : [];

    return (
        <XtramModal
            open={open}
            onClose={onClose}
            title={t('ViewTransactionMatch')}
            direction={direction}
            wide
            busy={isRemovingMatch}
            footer={
                <>
                    {match && onRemoveMatch && (
                        <XtramButton
                            variant="danger"
                            onClick={() => onRemoveMatch(match)}
                            disabled={loading || isRemovingMatch}
                            busy={isRemovingMatch}
                        >
                            {t('RemoveMatch')}
                        </XtramButton>
                    )}
                    <XtramButton variant="secondary" onClick={onClose} disabled={isRemovingMatch}>{t('close')}</XtramButton>
                </>
            }
        >
            {loading ? (
                <div className={xtramStyles.loading}><CircularProgress size={28} /></div>
            ) : !match ? (
                <p className={xtramStyles.emptyState}>{t('MatchDetailsNotFound')}</p>
            ) : (
                <>
                    <div className={xtramStyles.section}>
                        <div className={xtramStyles.card}>
                            <div className={styles.kvList}>
                                {details.map((item) => (
                                    <div key={item.label} className={styles.kv}>
                                        <span className={styles.kvLabel}>{item.label}</span>
                                        <span className={styles.kvValue}>{item.value}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className={xtramStyles.section}>
                        <h3 className={xtramStyles.sectionTitle}>{t('BankCommands')}</h3>
                        <TransactionRowsTable
                            rows={match.externalTransactions}
                            formatDate={formatDate}
                            t={t}
                            typeLabel="external"
                            amountColumnLabel={amountColumnLabel}
                        />
                    </div>
                    <div className={xtramStyles.section}>
                        <h3 className={xtramStyles.sectionTitle}>{t('BookCommands')}</h3>
                        <TransactionRowsTable
                            rows={match.internalTransactions}
                            formatDate={formatDate}
                            t={t}
                            typeLabel="internal"
                            amountColumnLabel={amountColumnLabel}
                        />
                    </div>
                </>
            )}
        </XtramModal>
    );
};

export default ViewTransactionMatchModal;
